import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";

import { LatLngLiteral } from "leaflet";

import {
  HvGlobalActions,
  HvTypography,
  HvGrid,
  HvInput,
  HvLoading,
} from "@hitachivantara/uikit-react-core";

import {
  IconData,
  geocodeLatLng,
  useIncidentTypes,
  geocodeAddress,
} from "lib/api";

import GenericMap from "components/common/GenericMap";

import useStyles from "./styles";
import FindCamera from "./FindCamera";

const Location: React.FC = () => {
  const { t } = useTranslation("incidentAddEdit");
  const classes = useStyles();

  const { values, setValues, setFieldValue, setFieldTouched, errors, touched } =
    useFormikContext<IncidentFormData>();

  const parseStatus = (field: keyof IncidentFormData, untouched = false) => {
    return errors[field] && (untouched || touched[field]) ? "invalid" : "valid";
  };

  const parseStatusMessage = (
    field: keyof IncidentFormData,
    untouched = false
  ) => {
    return errors[field] && (untouched || touched[field]) ? errors[field] : "";
  };

  const { data: typeData } = useIncidentTypes(undefined, {
    revalidateOnFocus: false,
  });

  const [icon, setIcon] = useState<IconData | undefined>();
  const [location, setLocation] = useState<LatLngLiteral | undefined>(
    undefined
  );

  useEffect(() => {
    const type = typeData?.data.find((v) => v.pk_inc_type_id === values.type);
    setIcon(type?.appearance);
  }, [typeData?.data, values.type]);

  const onChange = useCallback(
    async (newLoc: LatLngLiteral) => {
      setValues((existing) => ({
        ...existing,
        latitude: newLoc.lat,
        longitude: newLoc.lng,
      }));
    },
    [setValues]
  );

  const onSearch = useCallback(
    async (search: string) => {
      const geocode = await geocodeAddress(search);
      setValues((existing) => ({
        ...existing,
        latitude: geocode?.data.coordinates.lat,
        longitude: geocode?.data.coordinates.lng,
      }));
    },
    [setValues]
  );

  useEffect(() => {
    const lat = Number(values.latitude);
    const lng = Number(values.longitude);
    if (
      (values.latitude as unknown) !== "" &&
      !Number.isNaN(lat) &&
      (values.longitude as unknown) !== "" &&
      !Number.isNaN(lng)
    ) {
      setLocation({
        lat,
        lng,
      });
    } else {
      setLocation(undefined);
    }

    if (
      (values.latitude as unknown) === "" ||
      Number.isNaN(lat) ||
      (values.longitude as unknown) === "" ||
      Number.isNaN(lng)
    ) {
      setFieldValue("address", "");
    }
  }, [setFieldValue, values.latitude, values.longitude]);

  const [loadingAddress, setLoadingAddress] = useState(false);
  useEffect(() => {
    if (!location) return;

    const getAddress = async () => {
      try {
        const geocode = await geocodeLatLng(location);
        setFieldValue("address", geocode?.data.formattedAddress ?? "");
      } catch (e) {
        setFieldValue("address", "");
      }

      setLoadingAddress(false);
    };

    setLoadingAddress(true);
    getAddress();
  }, [location, setFieldValue]);

  const latitudeFilledIn = (values.latitude ?? "") !== "";
  const longitudeFilledIn = (values.longitude ?? "") !== "";

  return (
    <HvGrid container className={classes.root}>
      <HvGrid sm={12} item>
        <HvGlobalActions title={t("location.title")} variant="section" />
        <HvTypography variant="normalText">
          {t("location.description")}
        </HvTypography>
      </HvGrid>
      <HvGrid container item xs={12} lg={9}>
        <HvGrid xs={12} md={6} item>
          <GenericMap
            clickToMoveMarker
            dragToMoveMarker
            onChange={onChange}
            onSearch={onSearch}
            marker={icon}
            location={location}
            searchPlaceholder={t("location.map.searchPlaceholder")}
            searchButtonLabel={t("location.map.searchButtonLabel")}
            clearButtonLabel={t("location.map.clearButtonLabel")}
          />
        </HvGrid>
        <HvGrid container item xs={12} md={6} alignContent="flex-start">
          <HvGrid xs={12} item>
            <HvInput
              className={classes.input}
              label={t("location.fields.address")}
              readOnly
              placeholder={loadingAddress ? "" : "—"}
              value={loadingAddress ? "" : values.address ?? ""}
              status={loadingAddress ? "valid" : parseStatus("address", true)}
              statusMessage={parseStatusMessage("address", true)}
              endAdornment={loadingAddress && <HvLoading small />}
            />
          </HvGrid>
          <HvGrid xs={12} md={6} item>
            <HvInput
              className={classes.input}
              label={t("location.fields.latitude")}
              placeholder={
                longitudeFilledIn
                  ? ""
                  : t("location.fields.latitudePlaceholder")
              }
              inputProps={{
                autoComplete: "off",
              }}
              value={values.latitude?.toString() ?? ""}
              required={values.findCameras || longitudeFilledIn}
              status={parseStatus("latitude")}
              statusMessage={parseStatusMessage("latitude", values.findCameras)}
              onChange={(evt, value) => {
                setFieldValue("latitude", value);
              }}
              onBlur={() => {
                setFieldTouched("latitude");
              }}
            />
          </HvGrid>
          <HvGrid xs={12} md={6} item>
            <HvInput
              className={classes.input}
              label={t("location.fields.longitude")}
              placeholder={
                latitudeFilledIn
                  ? ""
                  : t("location.fields.longitudePlaceholder")
              }
              value={values.longitude?.toString() ?? ""}
              required={values.findCameras || latitudeFilledIn}
              status={parseStatus("longitude", values.findCameras)}
              statusMessage={parseStatusMessage("longitude")}
              onChange={(evt, value) => {
                setFieldValue("longitude", value);
              }}
              onBlur={() => {
                setFieldTouched("longitude");
              }}
            />
          </HvGrid>
          <FindCamera />
        </HvGrid>
      </HvGrid>
    </HvGrid>
  );
};

export default Location;
